'use client';

import FormLabel from '@c/forms/controls/FormLabel';
import FormSelect from '@c/forms/controls/FormSelect';
import AddAddressModal from '@c/modals/AddAddressModal';
import AddPaymentMethodModal from '@c/modals/AddPaymentMethodModal';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getShippingRegion } from '@util/firestore/cart';
import { updateOffer } from '@util/firestore/offers';
import { getPaymentMethods } from '@util/firestore/payments';
import {
  getProductById,
  getProductByIdNotClean,
} from '@util/firestore/products';
import { isDomestic } from '@util/firestore/shipengine';
import { getTax } from '@util/firestore/taxjar';
import { formatCurrency } from '@util/index';
import { logError } from '@util/logError';
import { db } from '@util/firebase';
import { doc, getDoc } from 'firebase/firestore';
import { useAuth } from 'context/AuthContext';
import { useToastContext } from 'context/ToastContext';
import { OfferDocument } from 'models/offer';
import { WarehouseDocument } from 'models/warehouse';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import * as z from 'zod';
import { useOffers } from '../useOffers';
import ItemShippingSelect from 'features/checkout/components/ItemShippingSelect';
import { buildPaymentOptions } from '@features/makeAnOffer/utils/paymentOptions';
import type { Rate } from '@util/firestore/shipengine';
import type { AddressDocument } from '@models/address';

const acceptOfferSchema = z.object({
  shipping_address: z.string().optional(),
  payment_method: z.string().optional(),
});

const formatAddressOption = (address: AddressDocument) =>
  `${address.address_line1} - ${address.city_locality}, ${address.state_province}, ${address.postal_code}`;

type AcceptOfferFormData = z.infer<typeof acceptOfferSchema>;

interface AcceptOfferContentProps {
  offer: OfferDocument;
  onClose: () => void;
  isSeller: boolean;
}

export default function AcceptOfferContent({
  offer,
  onClose,
  isSeller,
}: AcceptOfferContentProps) {
  if (isSeller) {
    return <AcceptOfferSellerContent offer={offer} onClose={onClose} />;
  }

  return <AcceptOfferBuyerContent offer={offer} onClose={onClose} />;
}

const AcceptOfferSellerContent = ({
  offer,
  onClose,
}: {
  offer: OfferDocument;
  onClose: () => void;
}) => {
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [bypassStockCheck, setBypassStockCheck] = useState(false);
  const { getNetEarnings, acceptThatOffer } = useOffers(offer.id);
  const { userDoc } = useAuth();
  const { showErrorToast } = useToastContext();
  const { data: product } = useQuery({
    queryKey: ['product', offer.product_id],
    queryFn: () => getProductById(offer.product_id),
    enabled: !!offer.product_id,
  });

  const isWarehouseProduct =
    product?.integration_info?.type === 'warehouse' &&
    !!product.integration_info.remote_id &&
    userDoc?.uid === 'E16WuBcn0UMejlp0FSdQulSSed12';

  const { data: warehouseDoc, isLoading: isWarehouseLoading } = useQuery({
    queryKey: ['warehouse', product?.integration_info?.remote_id],
    queryFn: async () => {
      const snap = await getDoc(
        doc(db, 'warehouse', product!.integration_info!.remote_id)
      );
      return snap.exists()
        ? ({ ...snap.data(), id: snap.id } as WarehouseDocument)
        : null;
    },
    enabled:
      isWarehouseProduct && userDoc?.uid === 'E16WuBcn0UMejlp0FSdQulSSed12',
  });

  const lowStockWpsItem = (() => {
    if (!warehouseDoc || !product?.mpn?.length) return null;
    const mpns = product.mpn;
    const matching = warehouseDoc.inventory.filter(
      (item) =>
        item.mpn &&
        mpns.includes(item.mpn) &&
        item.type === 'wps' &&
        item.qty > 0
    );
    if (!matching.length) return null;
    const cheapest = matching.sort(
      (a, b) => a.price - b.price || b.qty - a.qty
    )[0];
    return cheapest.qty <= 2 ? cheapest : null;
  })();

  return (
    <>
      <div className="my-8 flex flex-col items-center justify-center gap-y-8">
        <h3 className="w-[40rem] text-center text-[2rem] text-zinc-800">
          By accepting this offer, you agree to sell this item for{' '}
          <span className="font-bold text-black">
            {formatCurrency(offer.total)}
          </span>
        </h3>

        <div className="mt-4 flex w-full justify-center">
          <div className="flex items-center justify-center rounded-2xl bg-brand-lightest-gray px-4 py-2 text-[1.8rem] text-zinc-900">
            ({formatCurrency(offer.price)} +{' '}
            {formatCurrency(offer.shipping_cost)} Shipping +{' '}
            {formatCurrency(offer.tax)} Tax)
          </div>
        </div>

        <div className="flex flex-col items-center gap-y-4">
          <h3 className="w-[40rem] text-center text-[2rem] text-zinc-800">
            Your earnings for this order will be
          </h3>

          {product && (
            <h3 className="text-[3rem] font-bold text-black">
              {formatCurrency(getNetEarnings(offer))}
            </h3>
          )}
        </div>

        {isWarehouseProduct &&
          (isWarehouseLoading ? (
            <div className="w-full animate-pulse rounded-2xl bg-brand-lightest-gray px-6 py-4">
              <div className="h-8 w-1/2 rounded bg-zinc-200" />
              <div className="mt-3 h-6 w-3/4 rounded bg-zinc-200" />
            </div>
          ) : lowStockWpsItem ? (
            <div className="w-full rounded-2xl border border-yellow-400 bg-yellow-50 px-6 py-4">
              <p className="text-[1.6rem] font-semibold text-yellow-800">
                Low stock on WPS ({lowStockWpsItem.qty} remaining)
              </p>
              <label className="mt-3 flex cursor-pointer items-center gap-x-3">
                <input
                  type="checkbox"
                  checked={bypassStockCheck}
                  onChange={(e) => setBypassStockCheck(e.target.checked)}
                  className="h-6 w-6 cursor-pointer accent-yellow-600"
                />
                <span className="text-[1.6rem] text-yellow-800">
                  Bypass stock check and accept anyway
                </span>
              </label>
            </div>
          ) : null)}
      </div>

      <div className="flex w-full flex-col items-center justify-center bg-brand-lightest-gray p-12">
        <span className="text-[1.8rem] text-zinc-700">
          Once accepted, offers are binding and cannot be cancelled.
        </span>

        <div className="mt-8 flex w-full items-center gap-x-8">
          <button
            type="button"
            className="flex w-full items-center justify-center rounded-[1.5rem] border border-brand-black bg-inherit p-4 text-[1.8rem] font-semibold text-brand-black"
            onClick={onClose}
            disabled={loadingSubmit}
          >
            Cancel
          </button>

          <button
            type="button"
            className="flex w-full items-center justify-center rounded-[1.5rem] bg-[#1E7A1E] p-4 text-[1.8rem] font-semibold text-white disabled:bg-brand-gray disabled:text-black"
            onClick={async () => {
              setLoadingSubmit(true);
              try {
                await acceptThatOffer(bypassStockCheck || undefined);
                onClose();
              } catch (e) {
                showErrorToast((e as Error).message);
                onClose();
              }
            }}
            disabled={
              loadingSubmit || (isWarehouseProduct && isWarehouseLoading)
            }
          >
            {loadingSubmit ? 'Accepting...' : 'Accept'}
          </button>
        </div>
      </div>
    </>
  );
};

const AcceptOfferBuyerContent = ({
  offer,
  onClose,
}: {
  offer: OfferDocument;
  onClose: () => void;
}) => {
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [selectedRate, setSelectedRate] = useState<Rate | null>(null);
  const [ratesError, setRatesError] = useState<Error | null>(null);
  const [calculatingTax, setCalculatingTax] = useState(false);
  const [taxError, setTaxError] = useState<Error | null>(null);
  const [openModal, setOpenModal] = useState<'address' | 'payment' | ''>('');
  const queryClient = useQueryClient();
  const { acceptThatOffer } = useOffers(offer.id);
  const { data: paymentMethodData, isLoading } = useQuery({
    queryKey: ['paymentMethods'],
    queryFn: getPaymentMethods,
  });

  const { userDoc } = useAuth();

  const { data: product } = useQuery({
    queryKey: ['product-with-shipment', offer.product_id],
    queryFn: () => getProductByIdNotClean(offer.product_id),
    enabled: !!offer.product_id,
  });

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    setError,
    clearErrors,
    getValues,
    formState: { errors },
  } = useForm<AcceptOfferFormData>({
    resolver: zodResolver(acceptOfferSchema),
    defaultValues: {
      shipping_address: offer.address ? formatAddressOption(offer.address) : '',
      payment_method: offer.payment?.id || '',
    },
  });

  const form = watch();

  useEffect(() => {
    if (
      paymentMethodData &&
      paymentMethodData.length > 0 &&
      !offer.payment?.id &&
      !getValues('payment_method')
    ) {
      const defaultPaymentMethod =
        paymentMethodData[paymentMethodData.length - 1];
      setValue('payment_method', defaultPaymentMethod.id ?? '');
    }
  }, [offer.payment?.id, paymentMethodData, setValue, getValues]);

  useEffect(() => {
    if (selectedRate && selectedRate.rate_id !== offer.rate_id) {
      setCalculatingTax(true);
      getTax({
        product_id: offer.product_id,
        seller_id: offer.seller_id,
        to_address:
          offer.address ||
          userDoc?.addresses.find(
            (a) => formatAddressOption(a) === form.shipping_address
          )!,
        total: offer.price,
        shipping: selectedRate.total_amount,
      })
        .then((tax) => {
          updateOffer(offer.id, {
            tax: tax.amount,
            rate_id: selectedRate.rate_id,
            shipping_cost: selectedRate.total_amount,
            total: offer.price + tax.amount + selectedRate.total_amount,
          }).then(() => {
            queryClient.invalidateQueries({
              queryKey: ['offer', offer.id],
            });
            setCalculatingTax(false);
          });
        })
        .catch((e) => {
          setTaxError(e as Error);
          logError(e);
          setCalculatingTax(false);
        });
    } else if (product?.is_flat_rate) {
      const address = userDoc?.addresses.find(
        (a) => formatAddressOption(a) === form.shipping_address
      );
      if (address) {
        const region = getShippingRegion(address);
        const shipping_cost = product.shipping_costs?.find(
          (c) => c.code === region
        )?.cost;
        if (!shipping_cost) {
          return;
        }
        setCalculatingTax(true);
        getTax({
          product_id: offer.product_id,
          seller_id: offer.seller_id,
          to_address:
            offer.address ||
            userDoc?.addresses.find(
              (a) =>
                `${a.address_line1} - ${a.city_locality}, ${a.state_province}, ${a.postal_code}` ===
                form.shipping_address
            )!,
          total: offer.price,
          shipping: shipping_cost,
        }).then((tax) => {
          updateOffer(offer.id, {
            tax: tax.amount,
            shipping_cost,
            total: offer.price + tax.amount + shipping_cost,
          })
            .then(() => {
              queryClient.invalidateQueries({
                queryKey: ['offer', offer.id],
              });
              setCalculatingTax(false);
            })
            .catch((e) => {
              setTaxError(e as Error);
              logError(e);
              setCalculatingTax(false);
            });
        });
      }
    }
  }, [
    selectedRate,
    form.shipping_address,
    offer.rate_id,
    offer.address,
    offer.product_id,
    offer.seller_id,
    offer.price,
    offer.id,
    product?.is_flat_rate,
    product?.shipping_costs,
    userDoc?.addresses,
    queryClient,
  ]);

  const addressOptions =
    userDoc?.addresses?.map((address) => ({
      id: formatAddressOption(address),
      label: formatAddressOption(address),
      value: formatAddressOption(address),
    })) ?? [];

  const selectedPayment = paymentMethodData?.find(
    (method) =>
      method.id === form.payment_method || method.id === offer.payment?.id
  );

  const paymentOptions = buildPaymentOptions(paymentMethodData);

  const shipToAddress = userDoc?.addresses?.find(
    (a) => formatAddressOption(a) === form.shipping_address
  );

  const onFormSubmit = handleSubmit(async (data) => {
    if (loadingSubmit || calculatingTax) return;
    setLoadingSubmit(true);

    const selectedAddress = data.shipping_address
      ? userDoc?.addresses?.find(
          (a) => formatAddressOption(a) === data.shipping_address
        )
      : offer.address;

    if (!selectedPayment) {
      setLoadingSubmit(false);
      setError('payment_method', {
        type: 'manual',
        message: 'Please select a payment method',
      });
      return;
    }

    if (!selectedAddress?.address_line1) {
      setLoadingSubmit(false);
      setError('shipping_address', {
        type: 'manual',
        message: 'Please select a shipping address',
      });
      return;
    }

    if (
      !product?.is_flat_rate &&
      !(selectedRate || offer.rate_id) &&
      !isDomestic(product?.shipment, selectedAddress)
    ) {
      setLoadingSubmit(false);
      setError('root', {
        type: 'manual',
        message: 'Please select a shipping rate',
      });
      return;
    }

    const shippingCost = selectedRate?.total_amount ?? offer.shipping_cost ?? 0;
    const rateId = selectedRate?.rate_id ?? offer.rate_id;

    // finalize tax + total here so the buyer is always charged the current
    // shipping and tax, even when the cheapest rate was never changed
    let tax = offer.tax ?? 0;
    try {
      const taxRes = await getTax({
        product_id: offer.product_id,
        seller_id: offer.seller_id,
        to_address: selectedAddress,
        total: offer.price,
        shipping: shippingCost,
      });
      tax = taxRes.amount;
    } catch (e) {
      setLoadingSubmit(false);
      setTaxError(e as Error);
      logError(e);
      return;
    }

    const updatedOffer = {
      payment: selectedPayment,
      address: selectedAddress,
      shipping_cost: shippingCost,
      tax,
      total: offer.price + shippingCost + tax,
      ...(rateId && { rate_id: rateId }),
      ...(userDoc?.customer_id && { customer_id: userDoc.customer_id }),
    };
    try {
      await updateOffer(offer.id, updatedOffer);
      await acceptThatOffer();
      onClose();
    } catch (e) {
      logError(e);
      setLoadingSubmit(false);
    }
  });

  return (
    <form onSubmit={onFormSubmit}>
      <div className="my-8 flex flex-col items-center justify-center gap-y-8">
        <h2 className="text-center text-[2rem] font-semibold text-zinc-800">
          {product?.title}
        </h2>

        <h3 className="w-[40rem] text-center text-[2rem] text-zinc-800">
          By accepting this offer, you agree to buy this item for{' '}
          <span className="font-bold text-black">
            {formatCurrency(offer.total)}
          </span>
        </h3>

        <div className="mt-4 flex w-full justify-center">
          <div className="flex items-center justify-center rounded-2xl bg-brand-lightest-gray px-4 py-2 text-[1.8rem] text-zinc-900">
            ({formatCurrency(offer.price)} +{' '}
            {formatCurrency(offer.shipping_cost)} Shipping +{' '}
            {formatCurrency(offer.tax)} Tax)
          </div>
        </div>
      </div>

      {isLoading && !offer.payment?.id ? (
        <div className="relative h-[14rem] w-full p-8">
          <div className="h-[10rem] w-full animate-pulse rounded-2xl bg-brand-lightest-gray" />
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-[1.8rem] font-medium text-zinc-600">
              Loading Payment Methods...
            </span>
          </div>
        </div>
      ) : (
        <>
          {offer.is_exclusive && (
            <div className="space-y-8 p-8">
              <div className="flex flex-col gap-y-4">
                <div className="flex w-full items-center justify-between">
                  <span className="text-[1.8rem] font-medium text-black">
                    Shipping Address
                  </span>

                  <button
                    type="button"
                    onClick={() => setOpenModal('address')}
                    className="text-[1.5rem] font-medium text-brand-secondary"
                  >
                    Add
                  </button>
                </div>

                <Controller
                  name="shipping_address"
                  control={control}
                  render={({ field }) => (
                    <FormSelect
                      {...field}
                      disabled={!addressOptions.length}
                      placeholder="Select Shipping Address"
                      options={addressOptions}
                      error={!!errors.shipping_address}
                      control={control}
                      allowEmpty={false}
                      onChange={(e) => {
                        field.onChange(e.value);
                        if (!product?.is_flat_rate) {
                          setSelectedRate(null);
                        }
                      }}
                    />
                  )}
                />

                {errors.shipping_address && (
                  <span className="text-[1.5rem] text-brand-red">
                    {errors.shipping_address.message}
                  </span>
                )}
              </div>

              <div className="flex flex-col gap-y-4">
                <div className="flex w-full items-center justify-between">
                  <span className="text-[1.8rem] font-medium text-black after:ml-1 after:text-red-500 after:content-['*']">
                    Payment Method
                  </span>

                  <button
                    type="button"
                    onClick={() => setOpenModal('payment')}
                    className="text-[1.5rem] font-medium text-brand-secondary"
                  >
                    Add
                  </button>
                </div>

                {isLoading ? (
                  <div className="flex w-full flex-col gap-4">
                    <div className="h-[5.2rem] w-full animate-pulse rounded-[1.5rem] bg-gray-200" />
                  </div>
                ) : (
                  <FormSelect
                    name="payment_method"
                    control={control}
                    disabled={!paymentOptions.length}
                    allowEmpty={false}
                    placeholder="Select payment method"
                    options={paymentOptions}
                    error={!!errors.payment_method}
                    defaultValue={form.payment_method}
                  />
                )}

                {errors.payment_method && (
                  <span className="text-[1.5rem] text-brand-red">
                    {errors.payment_method.message}
                  </span>
                )}
              </div>

              {form.shipping_address &&
                product &&
                !product.is_flat_rate &&
                shipToAddress && (
                  <FormLabel value="Shipping">
                    <div className="max-h-[35rem] overflow-y-auto">
                      <ItemShippingSelect
                        chooseCheapestByDefault
                        hideProduct
                        product={product}
                        shipment={product.shipment}
                        shipTo={shipToAddress}
                        offer_amount={offer.price}
                        selectedRateId={selectedRate?.rate_id ?? null}
                        onOptionSelected={({ rate }) => setSelectedRate(rate)}
                        onError={(error) => {
                          setRatesError(error);
                        }}
                      />
                    </div>
                    {ratesError && (
                      <span className="mt-2 block text-[1.5rem] font-semibold text-brand-red">
                        {ratesError.message}
                      </span>
                    )}
                  </FormLabel>
                )}
            </div>
          )}
        </>
      )}

      {taxError && (
        <div className="flex w-full flex-col items-center justify-center bg-brand-lightest-gray p-12">
          <span className="text-[1.8rem] text-zinc-700">
            {taxError.message}
          </span>
        </div>
      )}

      <div className="flex w-full flex-col items-center justify-center bg-brand-lightest-gray p-12">
        <span className="text-[1.8rem] text-zinc-700">
          Once accepted, offers are binding and cannot be cancelled.
        </span>

        <div className="mt-8 flex w-full items-center gap-x-8">
          <button
            type="button"
            className="flex w-full items-center justify-center rounded-[1.5rem] border border-brand-black bg-inherit p-4 text-[1.8rem] font-semibold text-brand-black"
            onClick={onClose}
          >
            Cancel
          </button>

          <button
            type="submit"
            disabled={
              loadingSubmit ||
              calculatingTax ||
              !form.shipping_address ||
              !form.payment_method ||
              !!taxError ||
              (!!product &&
                !product.is_flat_rate &&
                !selectedRate &&
                !offer.rate_id &&
                !(shipToAddress && isDomestic(product.shipment, shipToAddress)))
            }
            className="flex w-full items-center justify-center rounded-[1.5rem] bg-[#1E7A1E] p-4 text-[1.8rem] font-semibold text-white disabled:bg-brand-gray disabled:text-black"
          >
            {loadingSubmit
              ? 'Accepting...'
              : calculatingTax
              ? 'Calculating tax...'
              : 'Accept'}
          </button>
        </div>
        {errors?.root && (
          <p className="mt-[1.3rem] text-red-500">{errors.root.message}</p>
        )}
      </div>

      {openModal === 'address' && (
        <AddAddressModal
          isOpen={true}
          dismiss={(address) => {
            setOpenModal('');
            if (address) {
              setValue('shipping_address', formatAddressOption(address));
              clearErrors('shipping_address');
              if (!product?.is_flat_rate) {
                setSelectedRate(null);
              }
            }
          }}
        />
      )}

      {openModal === 'payment' && (
        <AddPaymentMethodModal
          isOpen={true}
          dismiss={(paymentMethodId) => {
            setOpenModal('');
            if (paymentMethodId) {
              setValue('payment_method', paymentMethodId);
              clearErrors('payment_method');
            }
          }}
        />
      )}
    </form>
  );
};
